import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UseSessionTimeoutOptions {
  /** Timeout duration in minutes (default: 30) */
  timeoutMinutes?: number;
  /** Warning duration in minutes before logout (default: 2) */
  warningMinutes?: number;
  /** Whether the timeout is enabled (default: true) */
  enabled?: boolean;
}

export const useSessionTimeout = ({
  timeoutMinutes = 30,
  warningMinutes = 2,
  enabled = true,
}: UseSessionTimeoutOptions = {}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningShownRef = useRef(false);
  const isPageVisibleRef = useRef(true);
  const backgroundedAtRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    clearTimers();
    toast.info("You've been logged out due to inactivity", {
      duration: 5000,
    });
    await supabase.auth.signOut();
  }, [clearTimers]);

  const showWarning = useCallback(() => {
    if (!warningShownRef.current && isPageVisibleRef.current) {
      warningShownRef.current = true;
      toast.warning(
        `You'll be logged out in ${warningMinutes} minute${warningMinutes > 1 ? "s" : ""} due to inactivity`,
        {
          duration: warningMinutes * 60 * 1000,
          id: "session-warning",
        }
      );
    }
  }, [warningMinutes]);

  const resetTimer = useCallback(() => {
    if (!enabled || !isPageVisibleRef.current) return;

    clearTimers();
    warningShownRef.current = false;
    toast.dismiss("session-warning");

    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Set warning timer
    if (warningMinutes > 0 && warningMinutes < timeoutMinutes) {
      warningRef.current = setTimeout(showWarning, warningMs);
    }

    // Set logout timer
    timeoutRef.current = setTimeout(handleLogout, timeoutMs);
  }, [enabled, timeoutMinutes, warningMinutes, clearTimers, showWarning, handleLogout]);

  // Handle visibility changes (mobile tab switching, screen lock, etc.)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = async () => {
      const isVisible = document.visibilityState === "visible";
      
      if (isVisible && !isPageVisibleRef.current) {
        // Coming back from background
        isPageVisibleRef.current = true;
        const backgroundedAt = backgroundedAtRef.current;
        backgroundedAtRef.current = null;

        // Check how long we were backgrounded
        if (backgroundedAt) {
          const backgroundDuration = Date.now() - backgroundedAt;
          const maxBackgroundMs = timeoutMinutes * 60 * 1000;

          if (backgroundDuration > maxBackgroundMs) {
            // Was backgrounded longer than timeout - log out
            console.log("Session expired while app was in background");
            await handleLogout();
            return;
          }
        }

        // Verify session is still valid when returning
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error || !session) {
            console.log("Session invalid on return from background");
            // Session is gone, the auth state change listener will handle the UI
            return;
          }
          // Session is valid, restart the activity timer
          resetTimer();
        } catch (error) {
          console.error("Error checking session on visibility change:", error);
        }
      } else if (!isVisible && isPageVisibleRef.current) {
        // Going to background - pause timers
        isPageVisibleRef.current = false;
        backgroundedAtRef.current = Date.now();
        clearTimers();
        toast.dismiss("session-warning");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, timeoutMinutes, clearTimers, resetTimer, handleLogout]);

  useEffect(() => {
    if (!enabled) return;

    // Activity events to track
    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    // Throttle the reset to avoid excessive calls
    let lastReset = Date.now();
    const throttleMs = 1000; // Only reset once per second max

    const handleActivity = () => {
      if (!isPageVisibleRef.current) return;
      
      const now = Date.now();
      if (now - lastReset > throttleMs) {
        lastReset = now;
        resetTimer();
      }
    };

    // Start the initial timer
    resetTimer();

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearTimers();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, resetTimer, clearTimers]);

  return { resetTimer };
};
